import {Component, OnInit} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG, FCORG_BLACKLIST} from '../../../common/constants';
import {UserPreference} from '../../../models/user-preference.model';
import {FcorgService} from '../../../services/fcorg.service';
import {GfsUserService} from '../../../services/gfs-user.service';
import {StateService} from '../../state/state.service';

@Component({
    selector: 'app-user-preference',
    templateUrl: './user-preference.component.html',
    styleUrl: './user-preference.component.scss',
    standalone: false
})
export class UserPreferenceComponent implements OnInit {
  showSpinner: boolean;
  existingPreferences: UserPreference[] = [];
  primaryFcorg: string;
  fcorgOptions: string[] = [];

  constructor(readonly userService: GfsUserService,
              public stateService: StateService,
              public fcorgService: FcorgService,
              public snackBar: FabMessageService) {
  }

  async ngOnInit(): Promise<void> {
    this.getFcorgs();
    await this.getExistingPreferences();
    // Set the primary FCORG to the existing preference if it exists
    this.primaryFcorg = this.existingPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue;
  }

  /**
   * Event handler for saving user preferences
   */
  async onSave(): Promise<void> {
    // Save the preferences
    const userPreferences: UserPreference[] = [];
    userPreferences.push(await this.getPreferenceObject('Primary FCORG', this.primaryFcorg));
    this.userService.saveUserPreferences(userPreferences).subscribe({
      error: err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showSpinner = false;
        this.snackBar.displaySuccess('Preferences saved successfully');
      }
    });

    // Update the user preferences in the state service
    this.stateService.setUserPreferences(userPreferences);
  }

  /**
   * Fetches FCORGs for the user and filters out blacklisted FCORGs for the options for the Primary FCORG dropdown
   */
  getFcorgs(): void {
    this.showSpinner = true;
    this.fcorgService.getFcorgsById(this.stateService.getUserGmid()).subscribe(
      {
        next: (fcorgs) => {
          this.fcorgOptions = fcorgs.filter((fcorg: string) => !FCORG_BLACKLIST.includes(fcorg));
        },
        error: (error) => {
          this.showSpinner = false
          this.snackBar.displayError(error, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false
        }
      }
    );
  }

  /**
   * Pre-populate the preferences based off existing user preferences
   */
  async getExistingPreferences(): Promise<void> {
    this.showSpinner = true;
    return this.userService
    .getUserPreferences(this.stateService.getUserGmid()).toPromise().then((data) => {
      this.existingPreferences = data;
      this.showSpinner = false;
    }).catch((err) => {
      this.snackBar.displayError(err, DISMISS_MSG);
      this.showSpinner = false;
    });
  }

  /**
   * Gets a UserPreference object based on the provided key and value. If the existing preference already exists,
   * it updates the value of the existing preference.
   * @param preferenceKey string - The key for the user preference.
   * @param preferenceValue string - The value for the user preference.
   */
  getPreferenceObject(preferenceKey: string, preferenceValue: string): Promise<UserPreference> {
    return this.getExistingPreferences().then(() => {
      const existingPreference = this.existingPreferences.find(pref => pref.preferenceKey === preferenceKey);
      // If the preference already exists, update its value; otherwise, create a new preference object.
      if (existingPreference) {
        existingPreference.preferenceValue = preferenceValue;
        return existingPreference;
      } else {
        return {
          id: null,
          gmid: this.stateService.getUserGmid(),
          preferenceKey: preferenceKey,
          preferenceValue: preferenceValue
        };
      }
    });
  }
}
